import { KEYPAD_TYPES } from '$lib/constants/keypad';
import { LOGICBOARD_TYPES } from '$lib/constants/logic_board';
import { KEY_CARD_TYPES } from '$lib/constants/key_card';
import type { InputActionResponse } from '$lib/types/input_action';
import keypadBackground from '$lib/assets/keypad_background.svg';
import logicboardBackground from '$lib/assets/logicboard_background.svg';
import corridorPanelImage from '$lib/assets/corridor-panel.svg';
import keycardImage from '$lib/assets/key-card.svg';
import type { InputDeviceResponse } from '$lib/types/input_device';
import type { InputKeyResponse } from '$lib/types/input_key';
import type { DomainGraphLEDScene, LEDLoad, LEDScene } from '$lib/types/led_scene';

export type DeviceLayoutType = {
  key: string;
  label: string;
  num_keys: number;
  layout: ReadonlyArray<{ key_id: number; row: number; col: number }>;
  rows: number;
  cols: number;
  image: string;
};

export type InputActionOption = {
  actionId: number;
  address: number;
  keyIndex: number;
  eventType: 'on' | 'off';
  deviceName: string;
  keyName: string;
  label: string;
};

export type LedSceneIndexes = {
  scenesByTrigger: Map<string, DomainGraphLEDScene>;
  targetsByKey: Map<string, LEDLoad[]>;
  triggerScenesByKey: Map<string, DomainGraphLEDScene[]>;
};

export function sceneTriggerKey(address: number, keyIndex: number, eventType: string): string {
  return `${address}:${keyIndex}:${eventType}`;
}

export function targetKey(address: number, keyIndex: number): string {
  return `${address}:${keyIndex}`;
}

export function getDeviceLayout(device: InputDeviceResponse): DeviceLayoutType {
  if (device.type === 'corridor_keypad') {
    return KEYPAD_TYPES['corridor'];
  }
  if (device.type === 'logic_board') {
    const type = LOGICBOARD_TYPES[`${device.keys.length}key` as keyof typeof LOGICBOARD_TYPES];
    return type ?? LOGICBOARD_TYPES['1key'];
  }
  if (device.type === 'keycard') {
    return (
      KEY_CARD_TYPES[`${device.keys.length}key` as keyof typeof KEY_CARD_TYPES] ??
      KEY_CARD_TYPES['1key']
    );
  }
  const type = KEYPAD_TYPES[`${device.keys.length}key` as keyof typeof KEYPAD_TYPES];
  return type ?? KEYPAD_TYPES['1key'];
}

export function getDeviceTypeLabel(device: InputDeviceResponse): string {
  switch (device.type) {
    case 'corridor_keypad':
      return 'Corridor Panel';
    case 'logic_board':
      return 'Logic Board';
    case 'keycard':
      return 'Keycard';
    default:
      return `${device.keys.length}-Key Keypad`;
  }
}

export function getDeviceDisplayAddress(device: InputDeviceResponse): number {
  return device.keys[0]?.address ?? 0;
}

export function getDeviceChassisImage(device: InputDeviceResponse): string {
  switch (device.type) {
    case 'logic_board':
      return logicboardBackground;
    case 'corridor_keypad':
      return corridorPanelImage;
    case 'keycard':
      return keycardImage;
    default:
      return keypadBackground;
  }
}

export function findInputActionById(
  devices: InputDeviceResponse[],
  actionId: number
): InputActionOption | undefined {
  for (const device of devices) {
    for (const key of device.keys) {
      for (const action of key.actions) {
        if (action.id !== actionId) continue;
        if (action.event_type !== 'on' && action.event_type !== 'off') continue;
        const keyName = key.name || `Key ${key.key_index}`;
        return {
          actionId: action.id,
          address: key.address,
          keyIndex: key.key_index,
          eventType: action.event_type,
          deviceName: device.name,
          keyName,
          label: `${device.name} ${keyName} ${action.event_type.toUpperCase()}`
        };
      }
    }
  }
  return undefined;
}

export function buildLedSceneIndexes(
  scenes: DomainGraphLEDScene[],
  devices: InputDeviceResponse[]
): LedSceneIndexes {
  const scenesByTrigger = new Map<string, DomainGraphLEDScene>();
  const targetsByKey = new Map<string, LEDLoad[]>();
  const triggerScenesByKey = new Map<string, DomainGraphLEDScene[]>();

  for (const scene of scenes) {
    const action = findInputActionById(devices, scene.input_action_id);
    if (!action) continue;

    const trigger = sceneTriggerKey(action.address, action.keyIndex, action.eventType);
    scenesByTrigger.set(trigger, scene);

    const keyOnly = targetKey(action.address, action.keyIndex);
    const existing = triggerScenesByKey.get(keyOnly) ?? [];
    existing.push(scene);
    triggerScenesByKey.set(keyOnly, existing);

    for (const load of scene.led_loads) {
      const tk = targetKey(load.address, load.key_index);
      const loads = targetsByKey.get(tk) ?? [];
      loads.push(load);
      targetsByKey.set(tk, loads);
    }
  }

  return { scenesByTrigger, targetsByKey, triggerScenesByKey };
}

export function collectInputDevices(
  zones: Array<{ rooms: Array<{ input_devices: InputDeviceResponse[] }> }>
): InputDeviceResponse[] {
  return zones
    .flatMap((z) => z.rooms)
    .flatMap((r) => r.input_devices)
    .sort((a, b) => getDeviceDisplayAddress(a) - getDeviceDisplayAddress(b));
}

export function buildInputActionOptions(devices: InputDeviceResponse[]): InputActionOption[] {
  const options: InputActionOption[] = [];

  for (const device of devices) {
    for (const key of device.keys) {
      for (const action of key.actions) {
        if (action.event_type !== 'on' && action.event_type !== 'off') continue;
        const keyName = key.name || `Key ${key.key_index}`;
        options.push({
          actionId: action.id,
          address: key.address,
          keyIndex: key.key_index,
          eventType: action.event_type,
          deviceName: device.name,
          keyName,
          label: `${device.name} ${keyName} ${action.event_type.toUpperCase()}`
        });
      }
    }
  }

  return options.sort((a, b) => a.address - b.address || a.keyIndex - b.keyIndex);
}

export function findInputAction(
  devices: InputDeviceResponse[],
  address: number,
  keyIndex: number,
  eventType: 'on' | 'off'
): number | null {
  for (const device of devices) {
    for (const key of device.keys) {
      if (key.address !== address || key.key_index !== keyIndex) continue;
      const action = key.actions.find((a) => a.event_type === eventType);
      if (action) return action.id;
    }
  }
  return null;
}

export function findDeviceForKey(
  devices: InputDeviceResponse[],
  address: number
): InputDeviceResponse | undefined {
  return devices.find((d) => d.keys.some((k) => k.address === address));
}

export function findInputKey(
  devices: InputDeviceResponse[],
  address: number,
  keyIndex: number
): InputKeyResponse | undefined {
  for (const device of devices) {
    const key = device.keys.find((k) => k.address === address && k.key_index === keyIndex);
    if (key) return key;
  }
  return undefined;
}

export function countMappedKeysOnDevice(
  device: InputDeviceResponse,
  indexes: LedSceneIndexes
): number {
  let count = 0;
  for (const key of device.keys) {
    const tk = targetKey(key.address, key.key_index);
    const hasTrigger = (indexes.triggerScenesByKey.get(tk)?.length ?? 0) > 0;
    const hasTarget = (indexes.targetsByKey.get(tk)?.length ?? 0) > 0;
    if (hasTrigger || hasTarget) count++;
  }
  return count;
}

export function eventLabel(eventType: string): string {
  return eventType === 'on' ? 'ON' : eventType === 'off' ? 'OFF' : eventType.toUpperCase();
}

export function stateLabel(state: 0 | 1): string {
  return state === 1 ? 'On' : 'Off';
}

export function toDomainGraphScenes(scenes: LEDScene[]): DomainGraphLEDScene[] {
  return scenes.map((s) => ({
    id: s.id,
    input_action_id: s.input_action,
    is_stale: s.is_stale,
    is_manually_edited: s.is_manually_edited,
    led_loads: s.led_loads
  }));
}

export function ledSceneForKey(
  key: InputKeyResponse,
  eventType: 'on' | 'off',
  indexes: LedSceneIndexes
): DomainGraphLEDScene | undefined {
  const trigger = sceneTriggerKey(key.address, key.key_index, eventType);
  return indexes.scenesByTrigger.get(trigger);
}

export function formatLedTarget(
  load: LEDLoad,
  devices: InputDeviceResponse[]
): string {
  const targetKeyRow = findInputKey(devices, load.address, load.key_index);
  const name = targetKeyRow?.name?.trim() || `Key ${load.key_index}`;
  return `${name} · Addr ${load.address} · ${stateLabel(load.state)}`;
}

export function formatLedTargets(
  loads: LEDLoad[],
  devices: InputDeviceResponse[]
): string {
  if (loads.length === 0) return 'No targets';
  return loads.map((l) => formatLedTarget(l, devices)).join('; ');
}

export function keyHasLedScene(
  key: InputKeyResponse,
  indexes: LedSceneIndexes
): boolean {
  return (
    indexes.scenesByTrigger.has(sceneTriggerKey(key.address, key.key_index, 'on')) ||
    indexes.scenesByTrigger.has(sceneTriggerKey(key.address, key.key_index, 'off'))
  );
}
